// Settings.js - Updated with Add Tokens and Deduct Tokens

import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  Cog6ToothIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  UserIcon,
  SparklesIcon,
  TrophyIcon,
  FireIcon,
  WalletIcon,
  PlusCircleIcon,
  MinusCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { adminAPI } from '../../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('llm');
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [tokenStats, setTokenStats] = useState([]);
  const [addTokenAmount, setAddTokenAmount] = useState('');
  const [deductTokenAmount, setDeductTokenAmount] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [successUserId, setSuccessUserId] = useState(null);
  const [activeModalAction, setActiveModalAction] = useState('add'); // 'add' or 'deduct'

  const tabs = [
    // { id: 'llm', name: 'LLM Usage Control', icon: ChatBubbleLeftRightIcon },
    // { id: 'users', name: 'User Management', icon: UserGroupIcon },
    // { id: 'general', name: 'General', icon: Cog6ToothIcon },
    // { id: 'notifications', name: 'Notifications', icon: BellIcon },
    // { id: 'security', name: 'Security', icon: ShieldCheckIcon },
  ];

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSettings(),
        loadUsers(),
        loadTokenStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getUsers('all');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const loadTokenStats = async () => {
    try {
      const response = await adminAPI.getTokenStats();
      setTokenStats(response.data);
    } catch (error) {
      console.error('Failed to load token stats');
    }
  };

  const addUserTokens = async (userId, amount) => {
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return false;
    }
    
    setSaving(true);
    try {
      await adminAPI.addUserTokens(userId, { amount: parseInt(amount) });
      toast.success(`Added ${amount} tokens successfully`);
      setUpdateSuccess(true);
      setSuccessUserId(userId);
      setAddTokenAmount('');
      await Promise.all([loadUsers(), loadTokenStats()]);
      setTimeout(() => {
        setUpdateSuccess(false);
        setSuccessUserId(null);
      }, 2000);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add tokens');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deductUserTokens = async (userId, amount) => {
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return false;
    }
    
    const userTokenData = getUserTokenData(userId);
    if (amount > (userTokenData.available_token || 0)) {
      toast.error(`Cannot deduct more than available tokens (${userTokenData.available_token} available)`);
      return false;
    }
    
    setSaving(true);
    try {
      await adminAPI.deductUserTokens(userId, { amount: parseInt(amount) });
      toast.success(`Deducted ${amount} tokens successfully`);
      setUpdateSuccess(true);
      setSuccessUserId(userId);
      setDeductTokenAmount('');
      await Promise.all([loadUsers(), loadTokenStats()]);
      setTimeout(() => {
        setUpdateSuccess(false);
        setSuccessUserId(null);
      }, 2000);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to deduct tokens');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUserTokenData = (userId) => {
    return tokenStats.find(stat => stat.user_id === userId) || {
      available_token: 0,
      used_token: 0
    };
  };

  // LLM Usage Control Component
  const LLMUsageControl = () => {
    const totalAvailableTokens = tokenStats.reduce((acc, curr) => acc + (curr.available_token || 0), 0);
    const totalUsedTokens = tokenStats.reduce((acc, curr) => acc + (curr.used_token || 0), 0);
    const activeUsersToday = tokenStats.filter(u => u.used_token > 0).length;
    const lowTokenUsers = tokenStats.filter(u => (u.available_token || 0) < 100).length;

    return (
      <div className="space-y-8">
        {/* Token Statistics Overview */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Token System Overview</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 mb-1">Total Available Tokens</p>
                  <p className="text-2xl font-bold text-blue-700">{totalAvailableTokens.toLocaleString()}</p>
                </div>
                <WalletIcon className="h-8 w-8 text-blue-400" />
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 mb-1">Total Used Tokens</p>
                  <p className="text-2xl font-bold text-purple-700">{totalUsedTokens.toLocaleString()}</p>
                </div>
                <SparklesIcon className="h-8 w-8 text-purple-400" />
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 mb-1">Active Users Today</p>
                  <p className="text-2xl font-bold text-green-700">{activeUsersToday}</p>
                </div>
                <UserGroupIcon className="h-8 w-8 text-green-400" />
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 mb-1">Low Token Users (&lt;100)</p>
                  <p className="text-2xl font-bold text-yellow-700">{lowTokenUsers}</p>
                </div>
                <FireIcon className="h-8 w-8 text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* User Token Management Table */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Token Management</h3>
          
          <div className="mb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available Tokens</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Used Tokens</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Usage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const tokenData = getUserTokenData(user.id);
                  const availableTokens = tokenData.available_token || 0;
                  const usedTokens = tokenData.used_token || 0;
                  const totalTokens = availableTokens + usedTokens;
                  const usagePercent = totalTokens > 0 ? (usedTokens / totalTokens) * 100 : 0;
                  const isLowTokens = availableTokens < 100;
                  
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                            <span className="text-primary-600 font-bold text-sm">
                              {user.username?.[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{user.username}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          {updateSuccess && successUserId === user.id && (
                            <div className="ml-3 bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs flex items-center">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Updated!
                            </div>
                          )}
                        </div>
                       </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <WalletIcon className="h-4 w-4 text-green-500 mr-1" />
                          <span className="text-lg font-semibold text-green-600">{availableTokens.toLocaleString()}</span>
                        </div>
                        </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <SparklesIcon className="h-4 w-4 text-purple-500 mr-1" />
                          <span className="text-sm text-gray-700">{usedTokens.toLocaleString()}</span>
                        </div>
                        </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">{totalTokens.toLocaleString()}</span>
                        </td>
                      <td className="px-4 py-3">
                        <div className="w-24">
                          <div className="flex justify-between text-xs mb-1">
                            <span>{Math.round(usagePercent)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                usagePercent >= 90 ? 'bg-red-500' : 
                                usagePercent >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(usagePercent, 100)}%` }}
                            />
                          </div>
                        </div>
                        </td>
                      <td className="px-4 py-3">
                        {user.blocked ? (
                          <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">Blocked</span>
                        ) : isLowTokens ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-600 text-xs rounded-full">Low Tokens</span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">Active</span>
                        )}
                        </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setActiveModalAction('add');
                              setAddTokenAmount('');
                              setShowUserModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <PlusCircleIcon className="h-4 w-4 mr-1" />
                            Add
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setActiveModalAction('deduct');
                              setDeductTokenAmount('');
                              setShowUserModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <MinusCircleIcon className="h-4 w-4 mr-1" />
                            Deduct
                          </button>
                        </div>
                        </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Token Management Modal */}
        <Transition appear show={showUserModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => !saving && setShowUserModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                    {selectedUser && (
                      activeModalAction === 'add' ? (
                        <AddTokensModal
                          user={selectedUser}
                          tokenData={getUserTokenData(selectedUser.id)}
                          onAddTokens={addUserTokens}
                          onClose={() => setShowUserModal(false)}
                          saving={saving}
                          addTokenAmount={addTokenAmount}
                          setAddTokenAmount={setAddTokenAmount}
                        />
                      ) : (
                        <DeductTokensModal
                          user={selectedUser}
                          tokenData={getUserTokenData(selectedUser.id)}
                          onDeductTokens={deductUserTokens}
                          onClose={() => setShowUserModal(false)}
                          saving={saving}
                          deductTokenAmount={deductTokenAmount}
                          setDeductTokenAmount={setDeductTokenAmount}
                        />
                      )
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    );
  };

  // Add Tokens Modal Component
  const AddTokensModal = ({ 
    user, 
    tokenData, 
    onAddTokens, 
    onClose, 
    saving,
    addTokenAmount,
    setAddTokenAmount
  }) => {
    const [localAmount, setLocalAmount] = useState(addTokenAmount);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!localAmount || localAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      
      setIsSubmitting(true);
      const success = await onAddTokens(user.id, localAmount);
      setIsSubmitting(false);
      
      if (success) {
        setLocalAmount('');
        onClose();
      }
    };

    const availableTokens = tokenData.available_token || 0;
    const usedTokens = tokenData.used_token || 0;
    const totalTokens = availableTokens + usedTokens;

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg mx-auto">
            <span className="text-white font-bold text-xl">
              {user.username?.[0]?.toUpperCase()}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 text-lg mt-3">{user.username}</h3>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <p className="text-xs text-green-600 mb-1">Available</p>
            <p className="text-xl font-bold text-green-700">{availableTokens.toLocaleString()}</p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <p className="text-xs text-purple-600 mb-1">Used</p>
            <p className="text-xl font-bold text-purple-700">{usedTokens.toLocaleString()}</p>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <p className="text-xs text-blue-600 mb-1">Total</p>
            <p className="text-xl font-bold text-blue-700">{totalTokens.toLocaleString()}</p>
          </div>
        </div>

        <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
          <h4 className="font-medium text-green-800 mb-3 flex items-center">
            <PlusCircleIcon className="h-5 w-5 mr-2 text-green-600" />
            Add Tokens to User
          </h4>
          <div className="space-y-3">
            <input
              type="number"
              value={localAmount}
              onChange={(e) => setLocalAmount(e.target.value)}
              placeholder="Enter token amount"
              min="1"
              autoFocus
              className="w-full px-4 py-3 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-lg"
            />
            <button
              type="submit"
              disabled={saving || isSubmitting}
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
            >
              {(saving || isSubmitting) ? (
                <>
                  <ArrowPathIcon className="animate-spin h-5 w-5" />
                  Adding...
                </>
              ) : (
                <>
                  <PlusCircleIcon className="h-5 w-5" />
                  Add Tokens
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || isSubmitting}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors w-full"
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  // Deduct Tokens Modal Component
  const DeductTokensModal = ({ 
    user, 
    tokenData, 
    onDeductTokens, 
    onClose, 
    saving,
    deductTokenAmount,
    setDeductTokenAmount
  }) => {
    const [localAmount, setLocalAmount] = useState(deductTokenAmount);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const availableTokens = tokenData.available_token || 0;

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!localAmount || localAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (localAmount > availableTokens) {
        toast.error(`Cannot deduct more than available tokens (${availableTokens} available)`);
        return;
      }
      
      setIsSubmitting(true);
      const success = await onDeductTokens(user.id, localAmount);
      setIsSubmitting(false);
      
      if (success) {
        setLocalAmount('');
        onClose();
      }
    };

    const usedTokens = tokenData.used_token || 0;
    const totalTokens = availableTokens + usedTokens;

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg mx-auto">
            <span className="text-white font-bold text-xl">
              {user.username?.[0]?.toUpperCase()}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 text-lg mt-3">{user.username}</h3>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <p className="text-xs text-green-600 mb-1">Available</p>
            <p className="text-xl font-bold text-green-700">{availableTokens.toLocaleString()}</p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <p className="text-xs text-purple-600 mb-1">Used</p>
            <p className="text-xl font-bold text-purple-700">{usedTokens.toLocaleString()}</p>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <p className="text-xs text-blue-600 mb-1">Total</p>
            <p className="text-xl font-bold text-blue-700">{totalTokens.toLocaleString()}</p>
          </div>
        </div>

        <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
          <h4 className="font-medium text-red-800 mb-3 flex items-center">
            <MinusCircleIcon className="h-5 w-5 mr-2 text-red-600" />
            Deduct Tokens from User
          </h4>
          <div className="space-y-3">
            <input
              type="number"
              value={localAmount}
              onChange={(e) => setLocalAmount(e.target.value)}
              placeholder="Enter token amount"
              min="1"
              max={availableTokens}
              autoFocus
              className="w-full px-4 py-3 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white text-lg"
            />
            <p className="text-xs text-red-600">Maximum deduct: {availableTokens.toLocaleString()} tokens</p>
            <button
              type="submit"
              disabled={saving || isSubmitting || availableTokens === 0}
              className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
            >
              {(saving || isSubmitting) ? (
                <>
                  <ArrowPathIcon className="animate-spin h-5 w-5" />
                  Deducting...
                </>
              ) : (
                <>
                  <MinusCircleIcon className="h-5 w-5" />
                  Deduct Tokens
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || isSubmitting}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors w-full"
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">Configure your application settings and user controls</p>
        </div>

        {/* Settings Tabs */}
        {/* <div className="bg-white rounded-xl shadow-md p-2 mb-6 inline-flex flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors m-1 ${
                activeTab === tab.id
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </div> */}

        {/* Settings Content */}
        <div>
          {activeTab === 'llm' && <LLMUsageControl />}
          {activeTab === 'users' && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
              <UserGroupIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">User Management Settings</h3>
              <p className="text-gray-500">User management features coming soon...</p>
            </div>
          )}
          {activeTab === 'general' && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
              <Cog6ToothIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">General Settings</h3>
              <p className="text-gray-500">General settings coming soon...</p>
            </div>
          )}
          {/* {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
              <BellIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Notification Settings</h3>
              <p className="text-gray-500">Notification settings coming soon...</p>
            </div>
          )}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
              <ShieldCheckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Security Settings</h3>
              <p className="text-gray-500">Security settings coming soon...</p>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default Settings;